/**
 * 見積依頼の一覧 /rfqs（#119、docs/56 §6.4）。メニューには足さない（ホームのカード・案件のタブ・見積書の頭の帯「見積依頼 n件 ›」から来る）。
 * #218：?estimate=<見積> でその見積に付いた依頼だけ（見積書の頭の帯から）
 * タブ：返事待ち（期限の早い順＝期限超過が上）・回答あり・発注済・下書き・取消・すべて。探す（番号・{vendor}・{subject}）
 * #233 2a：?o=<{order}> でその{order}の元の依頼だけ（{order}の頭の帯「見積依頼 n件 ›」から）。行を選ぶと帯で「まとめて作成」（1件は「{order}作成」）
 */
import { ORDER_BUNDLE_MSG, RFQ_MSG, pickNameMapOf, type Estimate, type RfqList, type RfqListFilter, type RfqSummary } from "@genbaryoku/contract";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { api, messageOf } from "../../api.js";
import { BackButton, usePageName } from "../../components/BackButton.jsx";
import { FilterInput, ListHead } from "../../components/ListHead.jsx";
import { ListTabs, listTabPanel } from "../../components/ListTabs.jsx";
import { Icon } from "../../components/Shell.jsx";
import { CreateRfqDialog, rfqRowMenu } from "./GroupRfqs.jsx";
import { RfqPurgeDialog } from "./PurgeDialog.jsx";
import { RowMenu } from "../../components/RowMenu.jsx";
import { DOTS_H } from "../../components/HeadActions.jsx";
import { ErrorText } from "../../components/ui.jsx";
import { t, useLabels } from "../../labels.js";
import { md, mdTime, RfqStatus } from "./parts.jsx";
import { useMe } from "../../auth.js";
import { notify } from "../../components/Notice.jsx";
import { usePcOnly } from "../../lib/pcOnly.js";
import { useNarrow } from "../../lib/useNarrow.js";
import { BundleBar, OrderedMark, SelectBox, useOrderBundle } from "../orders/bundle.jsx";

const TABS: [RfqListFilter, string][] = [
  ["waiting", RFQ_MSG.tabWaiting],
  ["answered", RFQ_MSG.tabAnswered],
  ["ordered", ORDER_BUNDLE_MSG.filterOrdered],
  ["draft", RFQ_MSG.tabDraft],
  ["cancelled", RFQ_MSG.tabCancelled],
  ["all", RFQ_MSG.tabAll],
];

export function RfqListPage() {
  const L = useLabels();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const f = (TABS.find(([k]) => k === sp.get("f"))?.[0] ?? "waiting") as RfqListFilter;
  const [q, setQ] = useState(sp.get("q") ?? "");
  const [qApplied, setQApplied] = useState(q);
  const est = sp.get("estimate") ?? "";
  const ord = sp.get("o") ?? "";
  usePageName(RFQ_MSG.listTitle);
  useEffect(() => {
    const h = window.setTimeout(() => setQApplied(q.trim()), 300);
    return () => window.clearTimeout(h);
  }, [q]);
  const list = useQuery({
    queryKey: ["rfqs", f, qApplied, est, ord],
    queryFn: () => api<RfqList>("GET", `/rfqs?f=${f}${qApplied ? `&q=${encodeURIComponent(qApplied)}` : ""}${est ? `&e=${encodeURIComponent(est)}` : ""}${ord ? `&o=${encodeURIComponent(ord)}` : ""}`),
    placeholderData: keepPreviousData,
  });
  const items: RfqSummary[] = list.data?.items ?? [];
  /** 外注先の名前（#149）：略称。並んでいる中に同じ略称の別の会社があるときだけ「略称（正式名）」 */
  const vendorNames = pickNameMapOf(items.map((r) => ({ id: r.vendor.id, short: r.vendor.shortName, full: r.vendor.name })));
  const open = (id: string) => nav(`/rfqs/${id}`);
  // #233 2a：行を選ぶ（PC・モジュール sales）。送付した依頼だけ（下書き・取消は選べない）
  const me = useMe();
  const pc = usePcOnly();
  const narrow = useNarrow();
  const canBundle = !narrow && !pc.phone && !!me.data?.tenant.modules.includes("sales") && !pc.hidden("/orders/new");
  const [picked, setPicked] = useState<Map<string, RfqSummary>>(new Map());
  useEffect(() => setPicked(new Map()), [f, qApplied, est, ord]);
  const bundle = useOrderBundle();
  const pickable = (r: RfqSummary) => r.status === "sent" || r.status === "answered";
  // 「＋ 見積依頼書作成」（#241・★5）：{project}を選ぶ窓。見積から開いた一覧なら、その見積の{project}で始める
  const [creating, setCreating] = useState(false);
  const [purge, setPurge] = useState<RfqSummary | null>(null);
  const estGroup = useQuery({
    queryKey: ["estimate-group", est],
    queryFn: () => api<Estimate>("GET", `/estimates/${est}`),
    enabled: creating && !!est,
    select: (e) => (e.projectGroup ? { id: e.projectGroup.id, name: e.projectGroup.name } : null),
  });
  const createBtn = narrow ? null : (
    <button type="button" className="btn primary" onClick={() => setCreating(true)} data-testid="rfq-list-create">
      <Icon k="plus" />
      {RFQ_MSG.create}
    </button>
  );
  return (
    <ListHead
      back={<BackButton fallback={est ? { to: `/estimates/${est}`, label: "見積書" } : ord ? { to: `/orders/${ord}`, label: t("{order}") } : { to: "/", label: "ホーム" }} />}
      title={RFQ_MSG.listTitle}
    >
      {list.isError ? <ErrorText>{messageOf(list.error)}</ErrorText> : null}
      {canBundle ? (
        <BundleBar
          count={picked.size}
          busy={bundle.busy}
          onClear={() => setPicked(new Map())}
          onCreate={() => {
            const rs = [...picked.values()];
            bundle.start({ rfqIds: rs.map((r) => r.id) }, rs.flatMap((r) => r.orders)).catch((e) => notify({ text: messageOf(e), tone: "error" }));
          }}
        />
      ) : null}
      {bundle.dialog}
      {creating ? <CreateRfqDialog pickGroup initialGroup={estGroup.data ?? null} onClose={() => setCreating(false)} /> : null}
      {purge ?<RfqPurgeDialog rfq={purge} onClose={() => setPurge(null)} /> : null}
      <div className="lcard">
      <ListTabs
        id="rql"
        label="見積依頼を状態で絞る"
        tabs={TABS.map(([k, label]) => ({ key: k, label, count: list.data?.counts[k] ?? "…" }))}
        value={f}
        onChange={(k) => setSp((p) => {
          const n = new URLSearchParams(p);
          if (k === "waiting") n.delete("f");
          else n.set("f", k);
          return n;
        })}
        filter={<FilterInput value={q} onChange={setQ} placeholder={t(`番号・{vendor}・{subject}で絞り込み`)} testId="rfq-search" />}
        create={createBtn}
      />
      <div className="tbl-wrap ltab-panel" {...listTabPanel("rql", f)}>
        <table className="list" data-testid="rfq-list">
          <thead>
            <tr>
              {canBundle ? <th aria-label={ORDER_BUNDLE_MSG.selectRow} className="obsel-cell" /> : null}
              <th>{RFQ_MSG.colNo}</th>
              <th>{L.vendor}</th>
              <th>{`${L.subject}・${L.project}`}</th>
              <th>{RFQ_MSG.colReplyBy}</th>
              <th>{RFQ_MSG.colStatus}</th>
              <th>{RFQ_MSG.colSent}</th>
              {narrow ? null : <th aria-label="操作" />}
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="r" tabIndex={0} onClick={(e) => !(e.target as HTMLElement).closest("a,button,input") && open(r.id)} onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && open(r.id)} data-testid="rfq-row">
                {canBundle ? (
                  <td className="obsel-cell">
                    <SelectBox
                      on={picked.has(r.id)}
                      label={`${r.no ?? RFQ_MSG.draftNo} ${r.subject}`}
                      disabled={!pickable(r)}
                      onChange={(on) => setPicked((m) => {
                        const n = new Map(m);
                        if (on) n.set(r.id, r);
                        else n.delete(r.id);
                        return n;
                      })}
                    />
                  </td>
                ) : null}
                <td className="num">{r.no ?? <span className="muted">{RFQ_MSG.draftNo}</span>}</td>
                {/* 外注先は略称（#149）。同じ略称の別の会社が並ぶときだけ「略称（正式名）」。正式名は乗せたときも */}
                <td title={r.vendor.name} data-testid="rfq-row-vendor">
                  <b>{vendorNames.get(r.vendor.id) ?? r.vendor.shortName}</b>
                </td>
                {/* 件名・{project}（#241）：同じなら1行、違えば2行（{project}は小さく） */}
                <td data-testid="rfq-row-subject">
                  {r.subject || r.group?.name || <span className="muted">—</span>}
                  {r.group && r.subject && r.group.name !== r.subject ? <div className="muted" style={{ fontSize: 14 }}>{r.group.name}</div> : null}
                </td>
                <td className="num">{r.replyBy ? md(r.replyBy) : <span className="muted">—</span>}</td>
                <td className="statecell">
                  <RfqStatus s={r} compact />
                  <OrderedMark refs={r.orders} testId="rfq-ordered" />
                </td>
                <td className="num">{r.sentAt ? mdTime(r.sentAt).split(" ")[0] : <span className="muted">—</span>}</td>
                {/* #275：行の「⋯」（{project}のタブと同じ項目。取消した依頼はいちばん下に「削除」） */}
                {narrow ? null : (
                  <td className="grfq-more">
                    <RowMenu label={RFQ_MSG.lineMenu.replace("{name}", r.no ?? r.subject)} items={rfqRowMenu(r, "rfq-row", () => setPurge(r))} testId="rfq-row-more" trigger={DOTS_H} triggerClass="rowmore dots" />
                  </td>
                )}
              </tr>
            ))}
            {list.data && !items.length ? (
              <tr className="emptyrows">
                <td colSpan={(canBundle ? 7 : 6) + (narrow ? 0 : 1)}>{RFQ_MSG.listEmpty}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      </div>
    </ListHead>
  );
}
